import * as THREE from 'three';

import {
  START_POSITION,
  START_ROTATION,
  XR_CONFIG,
} from '../config.js';

const SESSION_MODE = 'immersive-vr';
const UNSUPPORTED_MESSAGE = 'Este dispositivo o navegador no es compatible con WebXR.';

function applyPosition(target, value) {
  if (Array.isArray(value)) {
    target.set(value[0] ?? 0, value[1] ?? 0, value[2] ?? 0);
    return;
  }

  target.set(value?.x ?? 0, value?.y ?? 0, value?.z ?? 0);
}

function applyRotation(target, value) {
  if (typeof value === 'number') {
    target.rotation.set(0, value, 0);
    return;
  }

  if (Array.isArray(value)) {
    target.rotation.set(value[0] ?? 0, value[1] ?? 0, value[2] ?? 0);
    return;
  }

  target.rotation.set(value?.x ?? 0, value?.y ?? 0, value?.z ?? 0);
}

function addFeature(target, feature) {
  if (feature && !target.includes(feature)) target.push(feature);
}

/**
 * Owns the WebXR session and the transformable player rig. The XR camera is
 * never moved directly; teleportation, reset and turning operate on playerRig.
 */
export class XRManager extends THREE.EventDispatcher {
  constructor({
    renderer,
    camera,
    scene,
    playerRig = null,
    entryButton = null,
    buttonContainer = null,
    desktopControls = null,
    sessionInit = null,
    domOverlayRoot = null,
    autoCreateButton = true,
    applyStartTransform = true,
    onSessionStart = null,
    onSessionEnd = null,
    onReset = null,
    onUnsupported = null,
    onError = null,
  } = {}) {
    super();

    if (!renderer?.xr) throw new TypeError('XRManager requiere un WebGLRenderer válido.');
    if (!camera?.isCamera) throw new TypeError('XRManager requiere una cámara válida.');
    if (!scene?.isScene) throw new TypeError('XRManager requiere una escena válida.');

    this.renderer = renderer;
    this.camera = camera;
    this.scene = scene;
    this.desktopControls = desktopControls;
    this.playerRig = playerRig ?? new THREE.Group();
    this.playerRig.name ||= 'PlayerRig';

    this.onSessionStart = onSessionStart;
    this.onSessionEnd = onSessionEnd;
    this.onReset = onReset;
    this.onUnsupported = onUnsupported;
    this.onError = onError;

    this.entryButton = null;
    this.supportMessage = null;
    this.isSupported = null;
    this._buttonOwned = false;
    this._disposed = false;
    this._supportCheck = null;
    this._sessionRequestPending = false;
    this._activeSession = null;

    this.renderer.xr.enabled = true;
    this.renderer.xr.setReferenceSpaceType(XR_CONFIG?.referenceSpaceType ?? 'local-floor');

    if (!this.playerRig.parent) this.scene.add(this.playerRig);
    if (applyStartTransform) {
      applyPosition(this.playerRig.position, START_POSITION);
      applyRotation(this.playerRig, START_ROTATION);
    }

    if (this.camera.parent !== this.playerRig) {
      this.scene.updateMatrixWorld(true);
      this.playerRig.attach(this.camera);
    }

    this._sessionInit = this._createSessionInit(sessionInit, domOverlayRoot);
    this._handleSessionStart = this._handleSessionStart.bind(this);
    this._handleSessionEnd = this._handleSessionEnd.bind(this);
    this._handleButtonClick = this._handleButtonClick.bind(this);

    this.renderer.xr.addEventListener('sessionstart', this._handleSessionStart);
    this.renderer.xr.addEventListener('sessionend', this._handleSessionEnd);

    if (entryButton) {
      this.attachEntryButton(entryButton);
    } else if (autoCreateButton && typeof document !== 'undefined') {
      this.createEntryButton(buttonContainer ?? document.body);
    }
  }

  get session() {
    return this.renderer.xr.getSession();
  }

  get isPresenting() {
    return this.renderer.xr.isPresenting;
  }

  _createSessionInit(overrides, domOverlayRoot) {
    const requiredFeatures = [...(XR_CONFIG?.requiredFeatures ?? [])];
    const optionalFeatures = [...(XR_CONFIG?.optionalFeatures ?? [])];
    const referenceSpaceType = XR_CONFIG?.referenceSpaceType ?? 'local-floor';

    if (referenceSpaceType !== 'viewer' && referenceSpaceType !== 'local') {
      addFeature(optionalFeatures, referenceSpaceType);
    }

    const merged = {
      ...overrides,
      requiredFeatures: [
        ...requiredFeatures,
        ...(overrides?.requiredFeatures ?? []),
      ],
      optionalFeatures: [
        ...optionalFeatures,
        ...(overrides?.optionalFeatures ?? []),
      ],
    };

    merged.requiredFeatures = [...new Set(merged.requiredFeatures)];
    merged.optionalFeatures = [...new Set(merged.optionalFeatures)]
      .filter((feature) => !merged.requiredFeatures.includes(feature))
      .filter((feature) => feature !== 'dom-overlay' || domOverlayRoot || overrides?.domOverlay);

    if (domOverlayRoot) {
      addFeature(merged.optionalFeatures, 'dom-overlay');
      merged.domOverlay = { root: domOverlayRoot };
    }

    return merged;
  }

  createEntryButton(container = document.body) {
    if (typeof document === 'undefined') return null;

    const button = document.createElement('button');
    button.type = 'button';
    button.id = 'xr-entry-button';
    button.className = 'xr-entry-button';
    button.setAttribute('aria-label', 'Entrar en realidad virtual');
    container?.appendChild(button);

    this._buttonOwned = true;
    this.attachEntryButton(button, true);
    return button;
  }

  attachEntryButton(button, owned = false) {
    if (!button) return null;

    if (this.entryButton) {
      this.entryButton.removeEventListener('click', this._handleButtonClick);
    }

    this.entryButton = button;
    this._buttonOwned = owned;
    this.entryButton.addEventListener('click', this._handleButtonClick);
    this.entryButton.disabled = true;
    this.entryButton.textContent = 'COMPROBANDO COMPATIBILIDAD VR…';
    this.entryButton.setAttribute('aria-busy', 'true');
    this.checkSupport();
    return this.entryButton;
  }

  checkSupport() {
    if (this._supportCheck) return this._supportCheck;

    if (typeof navigator === 'undefined' || !navigator.xr) {
      this._supportCheck = Promise.resolve(false);
    } else {
      this._supportCheck = navigator.xr.isSessionSupported(SESSION_MODE)
        .catch((error) => {
          this._reportError(error);
          return false;
        });
    }

    this._supportCheck.then((supported) => {
      if (this._disposed) return;
      this.isSupported = supported;

      if (supported) {
        this._setButtonState('ENTRAR EN REALIDAD VIRTUAL', false);
        this._setSupportMessage('');
      } else {
        this._setButtonState('WEBXR NO DISPONIBLE', true);
        this._setSupportMessage(UNSUPPORTED_MESSAGE);
        this.onUnsupported?.(UNSUPPORTED_MESSAGE, this);
        this.dispatchEvent({ type: 'unsupported', message: UNSUPPORTED_MESSAGE });
      }
    });

    return this._supportCheck;
  }

  async requestSession() {
    if (this.session) return this.session;
    if (this._sessionRequestPending) return null;

    // When invoked by a click, requestSession must run before transient user
    // activation expires. The entry button is only enabled after this flag is
    // true, so the normal path deliberately contains no preceding await.
    if (this.isSupported !== true) {
      this.checkSupport();
      return null;
    }
    if (this._disposed) return null;

    this._sessionRequestPending = true;
    this._setButtonState('INICIANDO REALIDAD VIRTUAL…', true);

    let session = null;
    try {
      session = await navigator.xr.requestSession(SESSION_MODE, this._sessionInit);
      if (this._disposed) {
        await session.end();
        return null;
      }
      await this.renderer.xr.setSession(session);
      if (this._disposed) {
        await session.end();
        return null;
      }
      return session;
    } catch (error) {
      this._reportError(error);
      if (session && !session.ended) {
        try {
          await session.end();
        } catch (endError) {
          this._reportError(endError);
        }
      }
      this._setButtonState('ENTRAR EN REALIDAD VIRTUAL', false);
      this._setSupportMessage('No se ha podido iniciar la sesión de realidad virtual.');
      return null;
    } finally {
      this._sessionRequestPending = false;
      this.entryButton?.setAttribute('aria-busy', 'false');
    }
  }

  async endSession() {
    const session = this.session;
    if (session) await session.end();
  }

  resetPlayerPosition() {
    applyPosition(this.playerRig.position, START_POSITION);
    applyRotation(this.playerRig, START_ROTATION);
    this.playerRig.updateMatrixWorld(true);

    this.onReset?.(this.playerRig, this);
    this.dispatchEvent({ type: 'reset', playerRig: this.playerRig });
    return this.playerRig;
  }

  _handleButtonClick() {
    if (this.session) {
      this.endSession().catch((error) => this._reportError(error));
    } else {
      this.requestSession();
    }
  }

  _handleSessionStart() {
    const session = this.session;
    this._activeSession = session;
    if (this.desktopControls) this.desktopControls.enabled = false;
    this._setButtonState('SALIR DE REALIDAD VIRTUAL', false);
    this._setSupportMessage('');

    this.onSessionStart?.(session, this);
    this.dispatchEvent({ type: 'sessionstart', session });
  }

  _handleSessionEnd() {
    const session = this._activeSession;
    this._activeSession = null;
    if (this.desktopControls) this.desktopControls.enabled = true;
    this._setButtonState('ENTRAR EN REALIDAD VIRTUAL', false);

    this.onSessionEnd?.(session, this);
    this.dispatchEvent({ type: 'sessionend', session });
  }

  _setButtonState(label, disabled) {
    if (!this.entryButton) return;
    this.entryButton.textContent = label;
    this.entryButton.disabled = disabled;
    this.entryButton.setAttribute('aria-busy', String(this._sessionRequestPending));
  }

  _setSupportMessage(message) {
    if (typeof document === 'undefined' || !this.entryButton) return;

    if (!message) {
      if (this.supportMessage) this.supportMessage.hidden = true;
      return;
    }

    if (!this.supportMessage) {
      this.supportMessage = document.createElement('p');
      this.supportMessage.className = 'xr-support-message';
      this.supportMessage.setAttribute('role', 'status');
      this.supportMessage.setAttribute('aria-live', 'polite');
      this.entryButton.insertAdjacentElement('afterend', this.supportMessage);
    }

    this.supportMessage.textContent = message;
    this.supportMessage.hidden = false;
  }

  _reportError(error) {
    console.error('[WebXR]', error);
    this.onError?.(error, this);
    this.dispatchEvent({ type: 'error', error });
  }

  dispose({ endSession = false, removeRig = false } = {}) {
    this._disposed = true;
    if (this.desktopControls) this.desktopControls.enabled = true;
    this.renderer.xr.removeEventListener('sessionstart', this._handleSessionStart);
    this.renderer.xr.removeEventListener('sessionend', this._handleSessionEnd);

    if (this.entryButton) {
      this.entryButton.removeEventListener('click', this._handleButtonClick);
      if (this._buttonOwned) this.entryButton.remove();
    }
    this.supportMessage?.remove();

    if (removeRig) this.playerRig.removeFromParent();
    if (endSession && this.session) {
      this.endSession().catch((error) => this._reportError(error));
    }
  }
}

export { UNSUPPORTED_MESSAGE };
export default XRManager;
